import { Injectable, inject } from "@angular/core";
import { InMemoryCache } from "@apollo/client/cache";
import { createHttpLink, from, split } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { AuthService } from "../auth/auth.service";
import { ClientTokenService } from "../auth/client-token.service";
import { EnvironmentService } from "../env/env.service";

@Injectable({
  providedIn: "root",
})
export class GraphqlService {
  private envService = inject(EnvironmentService);
  private authService = inject(AuthService);
  private clientToken = inject(ClientTokenService);

  public apolloOptions: any;
  private refreshingToken?: Promise<void>;
  /** The binding token that a server older than the binding model refused. A new token is tried again. */
  private refusedBinding: string | null = null;
  private uri = "";

  constructor() {
    this.envService
      .getApiInfo()
      .then((api) => (this.uri = api.graphql))
      .catch((err) => console.error(err));
  }

  createApollo() {
    this.envService
      .getApiInfo()
      .then((api) => (this.uri = api.graphql))
      .catch((err) => console.error(err));

    const getAuthHeaders = (headers: any) => {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${this.getToken()}`,
        },
      };
    };

    const authLink = setContext((_, { headers }) => getAuthHeaders(headers));
    const http = createHttpLink({
      uri: `https://${this.uri}`,
      fetch: (uri: any, options: any) => {
        return this.fetchWithTimeout(uri, options);
      },
    });

    // Create a WebSocket link:
    const ws = new GraphQLWsLink(
      createClient({
        url: `wss://${this.uri}`,
      })
    );

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
          console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
        );
      }
      if (networkError) {
        console.error(`[Network error]: ${networkError}`);
      }
    });

    const retryIf = async (error: any, operation: any) => {
      if (error.statusCode === 401) {
        await this.onUnauthorized(operation.getContext().headers?.authorization);
        operation.setContext(({ headers }) => getAuthHeaders(headers));
      }

      // retry codes: Unauthorized, Request Timeout, Too Many Requests, Internal Server Error, Bad Gateway, Service Unvailable, Gateway Timeout
      const doRetryCodes = [401, 408, 425, 429, 500, 502, 503, 504];
      return !!error && doRetryCodes.includes(error.statusCode);
    };

    const retryLink = new RetryLink({
      delay: {
        initial: 100,
        max: 2000,
        jitter: true,
      },
      attempts: async (count: number, operation: any, error: any) =>
        this.mayRetry(count, operation, error) && retryIf(error, operation),
    });

    const link = split(
      // split based on operation type
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === "OperationDefinition" && definition.operation === "subscription";
      },
      ws,
      http
    );
    this.apolloOptions = {
      link: from([authLink, retryLink, errorLink, link]),
      cache: new InMemoryCache(),
    };
    return this.apolloOptions;
  }

  private fetchWithTimeout(uri: any, options = {}) {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 15000);
    options["signal"] = controller.signal;
    return fetch(uri, options);
  }

  // app specific functions
  /**
   * The retry budget of one operation: three tries. A refused binding token
   * uses one of them, so that operation gets a fourth. A 5xx answer does not.
   * `count` is the number of tries made so far.
   */
  private mayRetry(count: number, operation: any, error: any): boolean {
    const sent = operation.getContext().headers?.authorization;
    if (error?.statusCode === 401 && this.bindingToken() && sent === `Bearer ${this.getToken()}`) {
      operation.setContext({ bindingRefused: true });
    }
    return count < (operation.getContext().bindingRefused ? 4 : 3);
  }

  /** `sent` is the authorization header of the refused request. */
  private async onUnauthorized(sent: string | undefined): Promise<void> {
    // The token changed while the request ran: the retry sends the current one.
    if (sent !== `Bearer ${this.getToken()}`) return;
    const binding = this.bindingToken();
    if (binding) {
      this.refusedBinding = binding;
      return;
    }
    if (!this.refreshingToken) {
      this.refreshingToken = this.refreshToken().finally(() => {
        this.refreshingToken = undefined;
      });
    }
    await this.refreshingToken;
  }

  private bindingToken(): string | null {
    const token = this.clientToken.getTokenSync();
    return token && token !== this.refusedBinding ? token : null;
  }

  /**
   * The binding token names the aircraft, so the server takes the tail from it.
   * Without one, or after a server refused it, the legacy device token serves.
   */
  private getToken() {
    return this.bindingToken() || this.authService.getDeviceToken();
  }

  private async refreshToken() {
    try {
      const authInfo = await this.authService.login_device(true);
      if (authInfo?.jwt) await this.authService.setLastDeviceConfig(authInfo);
    } catch (err) {
      console.error("refresh token: ", err);
    }
  }
}
